import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { DPO_EMAIL } from "@/lib/contact"
import type { PublicAttendanceOrganization } from "@/services/publicAttendance"

/** Politique de confidentialité publique (site Qualneo). */
export const PRIVACY_POLICY_URL = "https://www.qualneo.fr/confidentialite"

const CNIL_URL = "https://www.cnil.fr"

/**
 * Journal technique de Qualneo, sous le bouton de signature.
 * Qualneo en est responsable, pour la sécurité du service.
 * @example <QrSignaturePrivacyNotice />
 */
export function QrSignaturePrivacyNotice() {
  return (
    <p className="text-xs text-muted-foreground" data-test="qr-privacy-notice">
      Qualneo, éditeur de ce service, enregistre aussi des données techniques. Il s'agit de l'adresse IP et du
      navigateur. Finalité : la sécurité du service (intérêt légitime de Qualneo). Durée : 3 ans après la signature,
      puis suppression. Vous pouvez vous opposer :{" "}
      <a href={`mailto:${DPO_EMAIL}`} className="underline">
        {DPO_EMAIL}
      </a>
      . Réclamation possible auprès de la{" "}
      <a href={CNIL_URL} target="_blank" rel="noopener noreferrer" className="underline">
        CNIL
      </a>
      .{" "}
      <a
        href={PRIVACY_POLICY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="underline"
        data-test="qr-privacy-policy-link"
      >
        Politique de confidentialité de Qualneo
      </a>
    </p>
  )
}

/** Contact public de l'organisme : son e-mail, sinon son téléphone. */
function organizationContact(organization: PublicAttendanceOrganization) {
  return pipe(
    organization.publicContactEmail,
    O.map((email) => (
      <a href={`mailto:${email}`} className="underline" data-test="qr-controller-contact">
        {email}
      </a>
    )),
    O.alt(() =>
      pipe(
        organization.publicContactPhone,
        O.map((phone) => (
          <a href={`tel:${phone}`} className="underline" data-test="qr-controller-contact">
            {phone}
          </a>
        )),
      ),
    ),
  )
}

/**
 * Pied de page : l'organisme responsable de la signature.
 * @example <QrSignatureControllerNotice organization={organization} />
 */
export function QrSignatureControllerNotice({ organization }: { organization: PublicAttendanceOrganization }) {
  // Sans contact, le QR ne s'émet plus.
  // Un jeton déjà émis sert encore dix minutes.
  const rights = pipe(
    organizationContact(organization),
    O.map((contact) => <span>Pour vos droits RGPD, écrivez-lui : {contact}.</span>),
    O.toNullable,
  )
  const notice = pipe(
    organization.privacyNoticeUrl,
    O.map((url) => (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="underline"
        data-test="qr-controller-notice-link"
      >
        Notice de confidentialité de {organization.name}
      </a>
    )),
    O.toNullable,
  )
  return (
    <p className="space-x-1 text-xs text-muted-foreground" data-test="qr-controller-notice">
      <span>
        Votre organisme de formation, {organization.name}, est responsable de vos données. Il enregistre vos nom,
        signature et l'heure de signature. Finalité : attester votre présence à la formation.
      </span>
      {rights}
      <span>
        Réclamation possible auprès de la{" "}
        <a href={CNIL_URL} target="_blank" rel="noopener noreferrer" className="underline">
          CNIL
        </a>
        .
      </span>
      {notice}
    </p>
  )
}
